export type TUploadsValidationErrorKey =
  | "error.invalid_file_type"
  | "error.file_too_large"
  | "error.file_size_unknown";

export interface IFormatUploadSizeLabelParams {
  sizeInBytes: number;
}

// NOTE: NSAI-4790 — the divisors are a parameter because the same ladder serves
// two questions with different right answers: a file's size is decimal (the OS
// says so), an upload cap is binary (that is how the cap is defined and
// enforced). See formatUploadSizeLabel / formatUploadLimitLabel.
export interface IFormatSizeLadderProps {
  sizeInBytes: number;
  bytesInKilobyte: number;
  bytesInMegabyte: number;
  bytesInGigabyte: number;
}

export interface IValidateUploadFileParams {
  mimeType: string;
  size: number | null;
}

export interface IResolveUploadMaxSizeBytesParams {
  mimeType: string;
}

// NOTE: NSAI-5339 — how a caller of the integration picker states the size cap
// its own destination enforces, resolved per file because the meeting uploader's
// cap depends on whether the MIME is audio or video. The picker defaults to the
// chat attachment cap when no resolver is supplied, so chat and the assistant
// knowledge dropdown keep the limit they already had; the Uploads screens pass
// `resolveUploadMaxSizeBytes` so a recording pulled from Drive is held to the
// same limit as the identical file dragged in from disk.
export type TResolveAttachmentMaxSizeBytes = (
  props: IResolveUploadMaxSizeBytesParams,
) => number;

// NOTE: NSAI-4891 — the interpolation values the rejection copy needs, as
// display labels rather than raw byte counts, so every caller renders the same
// "250 MB" the dropzone advertises instead of formatting bytes for itself.
// `size` is absent when the platform gave us no size to report.
export interface IUploadValidationErrorParams {
  // NOTE: The index signature is what lets this be handed straight to i18next's
  // `t(key, params)` — its options parameter is typed as a `$Dictionary`, and a
  // plain interface without one is not assignable to it. Narrowed to `string`
  // so it cannot quietly become a bag of anything.
  [interpolationKey: string]: string | undefined;
  max: string;
  size?: string;
}

export interface IValidateFileResult {
  isValid: boolean;
  errorKey?: TUploadsValidationErrorKey;
  errorParams?: IUploadValidationErrorParams;
}

// NOTE: Lifecycle of a single file upload entry on the Uploads page.
//   FETCHING   — NSAI-5132: the file is being pulled out of a third-party
//                provider (Google Drive, OneDrive, …) into the client. No S3
//                traffic yet, and only integration picks pass through this
//                state — a local file already sits on the device, so it starts
//                at UPLOADING.
//   UPLOADING  — S3 PUT in flight, progress bar advancing
//   CONFIRMING — S3 done, server-side confirmUpload running
//   DONE       — confirmed; entry is removed from the list shortly after
//   ERROR      — terminal failure; errorMessage carries the server reason
//   CANCELLED  — user cancelled mid-upload
export enum EFileUploadStatus {
  FETCHING = "fetching",
  UPLOADING = "uploading",
  CONFIRMING = "confirming",
  DONE = "done",
  ERROR = "error",
  CANCELLED = "cancelled",
}

export interface IFileUploadEntry {
  id: string;
  fileName: string;
  progress: number;
  status: EFileUploadStatus;
  errorMessage?: string;
}

// NOTE: NSAI-5132 — one row of the Uploads progress list. A file picked from a
// third-party app is visible from the moment it is chosen: it enters the list
// as a FETCHING row owned by the integration layer, and once the bytes are in
// the client the same file continues as the UPLOADING entry the uploader owns.
// `IFileUploadEntry` is assignable to this, so both kinds render through one
// row component.
export interface IUploadProgressEntry {
  id: string;
  fileName: string;
  progress: number;
  status: EFileUploadStatus;
  errorMessage?: string;
  // NOTE: Bytes pulled from the provider so far. Only set while FETCHING, and
  // only load-bearing when the provider stream arrived without a
  // Content-Length: percent is unknowable then, and the byte count is the only
  // honest progress signal left.
  downloadedBytes?: number;
  // NOTE: Display name of the provider the file is coming from ("Google
  // Drive"). A brand name rather than translated copy, so it travels as data.
  sourceName?: string;
}

// NOTE: NSAI-5132 — a FETCHING row whose provider download is still in flight,
// so the row's cancel button can abort it.
export interface IIntegrationFetchEntry extends IUploadProgressEntry {
  cancel?: () => void;
}

// NOTE: NSAI-5132 — the `uploads` namespace keys the progress label can point
// at. A literal union rather than `string`: i18next types `t` against the
// resource bundles, so a loose key silently degrades its return type at every
// call site.
export type TUploadProgressI18nKey =
  | "progress.fetching"
  | "progress.preparing"
  | "table.processing_badge";

export interface IResolveUploadProgressLabelParams {
  status: EFileUploadStatus;
  progress: number;
  downloadedBytes?: number;
}

// NOTE: NSAI-5132 — what to print at the right edge of a progress row. Exactly
// one field is set: a translated phrase, a percentage, or a byte count for the
// Content-Length-less provider streams where percent does not exist.
export interface IUploadProgressLabel {
  i18nKey?: TUploadProgressI18nKey;
  percent?: number;
  sizeInBytes?: number;
}

// NOTE: NSAI-5241 — accepts the widest row shape so both the plain upload
// entries the web sidebar counts and the mixed upload/integration-fetch list
// the mobile screen keeps can be handed to the same counter.
export interface ICountUploadsInFlightParams {
  uploads: IUploadProgressEntry[];
}

export interface IUploadFileFromDeviceParams {
  uri: string;
  name: string;
  mimeType: string;
  fileSize: number;
  isAppOwnedFile?: boolean;
  onSettled?: () => void;
}

export interface IUploadAudioFileParams {
  file: File;
  fileName?: string;
}

// NOTE: NSAI-4874 — what an XHR `progress` event gives the upload reporter.
export interface IUploadProgressReporterEvent {
  loaded: number;
  total: number;
}

export interface ICreateUploadProgressReporterProps {
  onProgress: (progress: number) => void;
}

export interface IUploadFileToS3Props {
  uploadUrl: string;
  // NOTE: Blob for web. React Native XHR also accepts { uri, type, name } objects
  // that stream from disk without loading the entire file into memory.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any;
  contentType: string;
  onProgress?: (progress: number) => void;
  // NOTE: NSAI-5139 — does this platform actually emit `xhr.upload` progress?
  // iOS does not, so it passes `false` and the stall guard waits for a real
  // progress event instead of starting on send. Everyone else leaves it alone.
  hasUploadProgressEvents?: boolean;
  // NOTE: AbortSignal to allow caller to cancel the in-flight XHR upload
  signal?: AbortSignal;
  // NOTE: NSAI-6953 — standard padded base64 of the body's SHA-256 (44
  // characters), sent as the `x-amz-checksum-sha256` header. The header is the
  // only copy storage checks the bytes against and keeps; MinIO ignores the one
  // in the URL's query string. Pass it only with the value declared at
  // presign: the server signs the header into the URL, and storage refuses a
  // PUT that leaves it out or changes it. Without it the PUT sends
  // Content-Type alone, as before.
  checksumSha256?: string;
}

export interface IPresignFileUploadRequest {
  fileName: string;
  contentType: string;
  // NOTE: NSAI-4264 — optional declared size, checked server-side as a cheap
  // early rejection before the S3 round trip. Not authoritative on its own.
  fileSizeBytes?: number;
  signal?: AbortSignal;
  // NOTE: NSAI-6953 — forwarded from IPresignAndUploadFileProps.checksumSha256
  // so the presign callback can declare the digest the PUT will carry. Only
  // present when the caller passed one, and the callback must send it on: a
  // caller whose presign endpoint takes no checksum must not pass one at all,
  // because presignAndUploadFile puts it on the PUT either way.
  checksumSha256?: string;
}

export interface IPresignFileUploadResult {
  uploadUrl: string;
  s3Key: string;
}

// NOTE: Any domain API (chat, assistants, probes, …) that exposes a `presignFileUpload`
// method conforms to this shape — lets callers share the presign+upload pipeline.
export type TPresignFileUploadCallback = (
  params: IPresignFileUploadRequest,
) => Promise<IPresignFileUploadResult>;

export interface IPresignAndUploadFileProps {
  presign: TPresignFileUploadCallback;
  fileName: string;
  contentType: string;
  // NOTE: NSAI-4264 — forwarded to the presign call as-is; see
  // IPresignFileUploadRequest.fileSizeBytes.
  fileSizeBytes?: number;
  // NOTE: Blob for web; { uri, type, name } objects for React Native XHR uploads.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  body: any;
  onProgress?: (progress: number) => void;
  // NOTE: Forwarded to `uploadFileToS3`; see IUploadFileToS3Props.
  hasUploadProgressEvents?: boolean;
  // NOTE: Fires right after presign resolves and BEFORE the PUT begins.
  // Callers use this to stash the s3Key on their local attachment state so a
  // subsequent cancel / X handler can release it from S3 immediately. If the
  // PUT aborts, the stashed key lets the caller reach the backend cleanup
  // path instead of leaking the partial object.
  onPresignComplete?: (result: IPresignFileUploadResult) => void;
  signal?: AbortSignal;
  // NOTE: NSAI-6953 — one value for both steps: it goes to `presign` (see
  // IPresignFileUploadRequest.checksumSha256) and onto the PUT as a header (see
  // IUploadFileToS3Props.checksumSha256), so the declaration and the header
  // cannot disagree.
  checksumSha256?: string;
}

// NOTE: NSAI-4883 — `uploadFileToS3` knows exactly why an upload died: the
// storage service refused it, the connection dropped, or it went silent
// mid-transfer. Those three are indistinguishable to a user unless the reason
// survives the throw, so it rides along as a tag rather than only inside the
// English `Error.message` the UI cannot translate.
export enum EUploadFailureReason {
  REJECTED = "rejected",
  NETWORK = "network",
  STALLED = "stalled",
  // NOTE: NSAI-4583 — a presign refusal is a different class of failure from the
  // three above, which all describe an S3 PUT that started. These four say why
  // the upload was never accepted in the first place.
  TOO_LARGE = "too_large",
  UNSUPPORTED_TYPE = "unsupported_type",
  // NOTE: The caller lacks the permission the endpoint requires (the assistant
  // presign is gated on *_ASSISTANT_WRITE). Kept separate from REJECTED because
  // telling someone to "attach a different file" when the file was never the
  // problem sends them in circles.
  NOT_PERMITTED = "not_permitted",
  // NOTE: The honest answer when no signal explains the failure.
  UNKNOWN = "unknown",
}

// NOTE: the literal union, not `string` — i18next types `t` against the
// resource bundles, so a loose key silently degrades its return type to
// `unknown` at every call site and the mismatch surfaces there instead of here.
export type TUploadFailureI18nKey =
  | "common:upload_error.rejected"
  | "common:upload_error.network"
  | "common:upload_error.stalled"
  | "common:upload_error.too_large"
  | "common:upload_error.unsupported_type"
  | "common:upload_error.not_permitted"
  | "common:upload_error.unknown";

// NOTE: the index signature is what lets these be handed straight to
// `i18next.t` as interpolation values — it types its options against
// `$Dictionary`, which a closed interface does not satisfy.
export interface IUploadFailureI18nParams {
  [key: string]: number | undefined;
  status?: number;
  seconds?: number;
}

export interface IUploadFailureError extends Error {
  uploadFailure: EUploadFailureReason;
  uploadFailureParams: IUploadFailureI18nParams;
}

export interface ICreateUploadFailureErrorProps {
  reason: EUploadFailureReason;
  // NOTE: stays English and technical — it is what lands in the log and in a
  // "view details" disclosure. The user-facing sentence comes from `i18nKey`.
  message: string;
  params?: IUploadFailureI18nParams;
}

export interface IUploadFailureDetails {
  reason: EUploadFailureReason;
  i18nKey: TUploadFailureI18nKey;
  i18nParams: IUploadFailureI18nParams;
}

export interface IPresignAndUploadFileResult {
  s3Key: string;
}

// NOTE: NSAI-4583 — a presign refusal arrives as a plain axios error, not as one
// of the tagged pipeline failures above, so its reason has to be derived from the
// status. The file's own size rides along because it is the only signal that can
// explain a refusal the status leaves ambiguous: the presign DTO answers 400 for
// both an oversized `fileSizeBytes` and a disallowed `contentType`.
export interface IClassifyUploadFailureProps {
  error: unknown;
  // NOTE: Files pulled in through an integration never pass the drag-and-drop or
  // picker size guards, which is the path that produced the unexplained failure
  // in this ticket.
  fileSizeBytes?: number;
  maxFileSizeBytes?: number;
}

export interface IResolveUploadFailureDisplayProps {
  failures: IUploadFailureDetails[];
}

// NOTE: NSAI-4583 — the localized copy an integration download failure can be
// reported with. Shared so web and mobile map a status to the same sentence
// instead of one client echoing the backend's raw `message` field.
export type TAttachmentDownloadErrorKey =
  | "attachments.file_access_denied"
  | "attachments.file_access_not_found"
  | "attachments.file_access_too_large";

// NOTE: NSAI-5132 — the shape both clients' HTTP layers report a download with
// (axios' `AxiosProgressEvent` is assignable to it). Structural rather than an
// axios import so the reporter below stays testable without one, and `total` is
// optional — not `number | undefined` like the chat download event — because
// axios declares it optional and an optional source property does not satisfy a
// required one.
export interface IDownloadProgressReporterEvent {
  loaded: number;
  total?: number;
}

// NOTE: NSAI-5132 — one throttled report. `progress` is absent for a stream
// with no Content-Length: percent does not exist then, and writing a 0 would
// park the UI at a stuck "0%" for the whole download.
export interface IDownloadProgressUpdate {
  downloadedBytes: number;
  progress?: number;
}

export interface ICreateDownloadProgressReporterProps {
  onProgress: (update: IDownloadProgressUpdate) => void;
}

// NOTE: NSAI-5132 — the attachment fields a download report writes to. Declared
// structurally for the same reason as IIntegrationFetchAttachment: each client
// owns its own `IAttachment`, and this is the part of it a progress tick is
// allowed to touch.
export interface IProgressTrackedAttachment {
  id: string;
  progress?: number;
  downloadedBytes?: number;
}

export interface IApplyDownloadProgressProps<
  TAttachment extends IProgressTrackedAttachment,
> {
  attachments: TAttachment[];
  id: string;
  downloadedBytes: number;
  // NOTE: Absent for a stream with no Content-Length — see
  // IDownloadProgressUpdate. Left untouched on the attachment in that case
  // rather than written as 0.
  progress?: number;
}

// NOTE: NSAI-5132 — the part of a client attachment a fetch row is built from.
// Declared structurally because web and mobile each own their `IAttachment`
// (and their own provider enum, which is why `provider` is a string here — the
// wire values are what the two agree on).
export interface IIntegrationFetchAttachment {
  id: string;
  name: string;
  provider: string;
  progress?: number;
  downloadedBytes?: number;
  cancel?: () => void;
}

export interface IBuildIntegrationFetchEntriesProps {
  // NOTE: Already narrowed to the downloading ones by the caller — the
  // attachment status enum is per-platform, so the filter stays there.
  attachments: IIntegrationFetchAttachment[];
}

export interface IGetIntegrationFetchSignatureProps {
  entries: IIntegrationFetchEntry[];
}

export interface IPruneProcessedAttachmentIdsProps {
  processedIds: Set<string>;
  currentIds: Set<string>;
}

export interface IReportIntegrationFetchProps {
  // NOTE: Already narrowed to the downloading ones by the caller — see
  // IBuildIntegrationFetchEntriesProps.
  attachments: IIntegrationFetchAttachment[];
  onChange: (entries: IIntegrationFetchEntry[]) => void;
}

// NOTE: NSAI-5132 — the attachment-state setter a download progress writer is
// bound to. Only the updater form is accepted, which is all the writer needs and
// keeps React's `Dispatch<SetStateAction<T>>` out of this package.
export interface ICreateDownloadProgressWriterProps<
  TAttachment extends IProgressTrackedAttachment,
> {
  setAttachments: (
    updater: (currentAttachments: TAttachment[]) => TAttachment[],
  ) => void;
}

export interface IWriteDownloadProgressProps {
  id: string;
  downloadedBytes: number;
  // NOTE: Absent for a stream with no Content-Length — see
  // IApplyDownloadProgressProps.
  progress?: number;
}

export interface IResolveUploadRowActionsParams {
  status: EFileUploadStatus;
}

// NOTE: NSAI-5132 — what a progress row's chrome may do in a given state:
// FETCHING draws the download glyph and its abort is a fetch abort, a transfer
// still running can be cancelled, and a finished row can only be dismissed.
export interface IUploadRowActions {
  isFetching: boolean;
  isCancellable: boolean;
  isDismissable: boolean;
}

// NOTE: NSAI-5144 — inputs for picking which timestamp the Uploads row shows
// on its completion badge. Mirrors the two fields of IUploadedMeetingSummary
// that carry a date, so callers can hand the summary straight in.
export interface IResolveUploadedMeetingCompletionDateParams {
  processedAt: string | null;
  createdAt: string;
}

export interface IUploadedMeetingCompletionDate {
  dateString: string;
  // NOTE: False means the row has no recorded completion time and `dateString`
  // is the upload date instead — the UI has to label it as such rather than
  // claiming the recording was processed then.
  isProcessed: boolean;
}

// NOTE: NSAI-5560 — what makes two picks "the same file" before either has been
// read. `File.name` + `File.size` is everything both platforms know at the
// moment the user chooses, and a content hash is not: web hashes inside the
// upload run and skips it past MAX_UPLOAD_CONTENT_HASH_SIZE_BYTES, mobile picks
// by URI. The server still hashes the bytes it receives, so this is the cheap
// front gate, not the authority.
export interface IUploadIdentity {
  fileName: string;
  fileSizeBytes: number;
}

export interface IIsSameUploadInFlightProps {
  // NOTE: The uploads that have neither succeeded nor failed yet. Callers hold
  // these as a per-upload map keyed by upload id, hence an iterable rather than
  // the rendered entry list — a DONE or ERROR row is finished and its file may
  // be uploaded again.
  inFlightUploads: Iterable<IUploadIdentity>;
  candidate: IUploadIdentity;
}

// NOTE: NSAI-5732 — inputs for the "View details" technical line. The text is
// passed in rather than translated inside because frontend-shared holds no
// i18n instance of its own; each app supplies its own "no response from the
// server" wording.
export interface IDescribeRequestErrorDetailsProps {
  error: unknown;
  noResponseText: string;
}
